import {
  installThreatIntelFingerprints,
  installThreatIntelRecords,
  type KnownFingerprintRecord,
  type KnownHashRecord,
} from "./threat-intel";
import type { HashAlgorithm } from "./types";

// v1 carried exact hashes only, which cannot see a repack. v2 adds structural
// fingerprints alongside them; the two are independent, so a pack may ship
// either or both. The schema string is echoed back verbatim rather than
// upgraded in place, because the Ed25519 signature is computed over the
// canonical form of this object and rewriting the field would invalidate it.
export type ThreatIntelSchema = "jarlab.intel.v1" | "jarlab.intel.v2";

const SUPPORTED_SCHEMAS: ThreatIntelSchema[] = [
  "jarlab.intel.v1",
  "jarlab.intel.v2",
];

function isSupportedSchema(value: unknown): value is ThreatIntelSchema {
  return SUPPORTED_SCHEMAS.includes(value as ThreatIntelSchema);
}

export interface ThreatIntelPackFingerprint {
  family: string;
  variant: string;
  simhash64: string;
  // How close the sample must be, as a percentage, before the pack author is
  // willing to stand behind the attribution. Anything below it is not a match.
  threshold: number;
  source?: string;
  reference: string;
  firstSeen?: string;
}

export interface ThreatIntelPack {
  schema: ThreatIntelSchema;
  id: string;
  title: string;
  version: string;
  issuedAt: string;
  expiresAt?: string;
  publicKey?: string;
  signature?: string;
  records: Array<{
    id: string;
    family: string;
    variant: string;
    hashes: Partial<Record<HashAlgorithm, string>>;
    source?: string;
    reference: string;
    firstSeen?: string;
  }>;
  fingerprints?: ThreatIntelPackFingerprint[];
  // Analyst-authored YARA rules, distributed under the same signature as the
  // hashes. `trusted` on these follows the pack, so a rule from a verified
  // pack is treated as stronger evidence than one pasted in by hand.
  yara?: ThreatIntelPackYara[];
}

export interface ThreatIntelPackYara {
  namespace: string;
  rules: string;
}

export interface ImportedIntelPack {
  pack: ThreatIntelPack;
  records: KnownHashRecord[];
  fingerprints: KnownFingerprintRecord[];
  yara: Array<{ namespace: string; rules: string; trusted: boolean }>;
  signatureValid: boolean;
  trusted: boolean;
  expired: boolean;
  warnings: string[];
}

function stable(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stable(object[key])}`)
    .join(",")}}`;
}

function base64Bytes(value: string) {
  const clean = value.replace(/^ed25519:/i, "").replace(/\s+/g, "");
  const binary = globalThis.atob(clean);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function unsignedPayload(pack: ThreatIntelPack) {
  const unsigned = Object.fromEntries(
    Object.entries(pack).filter(([key]) => key !== "signature"),
  );
  return new TextEncoder().encode(stable(unsigned));
}

async function verifyEd25519(
  pack: ThreatIntelPack,
  publicKey: string,
): Promise<boolean> {
  if (!pack.signature) return false;
  try {
    const key = await globalThis.crypto.subtle.importKey(
      "raw",
      base64Bytes(publicKey),
      { name: "Ed25519" },
      false,
      ["verify"],
    );
    return globalThis.crypto.subtle.verify(
      { name: "Ed25519" },
      key,
      base64Bytes(pack.signature),
      unsignedPayload(pack),
    );
  } catch {
    return false;
  }
}

function cleanText(value: unknown, label: string, max = 240) {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`${label} must be a non-empty string.`);
  }
  return value.trim().slice(0, max);
}

function cleanHash(algorithm: HashAlgorithm, value: unknown) {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  const length = { sha256: 64, sha1: 40, md5: 32 }[algorithm];
  return new RegExp(`^[a-f0-9]{${length}}$`).test(normalized)
    ? normalized
    : undefined;
}

function cleanSimhash(value: unknown) {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  return /^[a-f0-9]{16}$/.test(normalized) ? normalized : undefined;
}


function cleanThreshold(value: unknown) {
  const numeric = typeof value === "number" ? value : Number.NaN;
  // A threshold has to be a usable similarity score, otherwise a typo would
  // silently match everything (0) or nothing (100).
  return Number.isFinite(numeric) && numeric > 0 && numeric <= 100
    ? Math.round(numeric * 10) / 10
    : undefined;
}

function normalizeFingerprints(value: unknown): ThreatIntelPackFingerprint[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 10_000).flatMap((raw, index) => {
    const record = raw as Partial<ThreatIntelPackFingerprint>;
    const simhash64 = cleanSimhash(record.simhash64);
    const threshold = cleanThreshold(record.threshold);
    if (!simhash64 || threshold === undefined) return [];
    return [
      {
        family: cleanText(record.family, `Fingerprint ${index + 1} family`, 120),
        variant: cleanText(
          record.variant,
          `Fingerprint ${index + 1} variant`,
          200,
        ),
        simhash64,
        threshold,
        ...(record.source
          ? {
              source: cleanText(
                record.source,
                `Fingerprint ${index + 1} source`,
                200,
              ),
            }
          : {}),
        reference: cleanText(
          record.reference,
          `Fingerprint ${index + 1} reference`,
          1_000,
        ),
        ...(record.firstSeen
          ? {
              firstSeen: cleanText(
                record.firstSeen,
                `Fingerprint ${index + 1} first-seen date`,
                80,
              ),
            }
          : {}),
      },
    ];
  });
}

function normalizePack(input: unknown): ThreatIntelPack {
  if (!input || typeof input !== "object")
    throw new Error("The intelligence pack is not a JSON object.");
  const value = input as Partial<ThreatIntelPack>;
  if (!isSupportedSchema(value.schema))
    throw new Error("Unsupported intelligence-pack schema.");
  if (!Array.isArray(value.records))
    throw new Error("The intelligence pack has no records array.");
  const pack: ThreatIntelPack = {
    schema: value.schema,
    id: cleanText(value.id, "Pack ID", 120),
    title: cleanText(value.title, "Pack title", 160),
    version: cleanText(value.version, "Pack version", 80),
    issuedAt: cleanText(value.issuedAt, "Issued date", 80),
    ...(value.expiresAt
      ? { expiresAt: cleanText(value.expiresAt, "Expiry date", 80) }
      : {}),
    ...(value.publicKey
      ? { publicKey: cleanText(value.publicKey, "Public key", 500) }
      : {}),
    ...(value.signature
      ? { signature: cleanText(value.signature, "Signature", 500) }
      : {}),
    records: value.records.slice(0, 10_000).map((raw, index) => {
      const record = raw as ThreatIntelPack["records"][number];
      const hashes = {
        sha256: cleanHash("sha256", record.hashes?.sha256),
        sha1: cleanHash("sha1", record.hashes?.sha1),
        md5: cleanHash("md5", record.hashes?.md5),
      };
      const filtered = Object.fromEntries(
        Object.entries(hashes).filter(([, hash]) => Boolean(hash)),
      ) as Partial<Record<HashAlgorithm, string>>;
      if (!Object.keys(filtered).length) {
        throw new Error(`Record ${index + 1} has no valid malware hash.`);
      }
      return {
        id: cleanText(record.id, `Record ${index + 1} ID`, 160),
        family: cleanText(record.family, `Record ${index + 1} family`, 120),
        variant: cleanText(record.variant, `Record ${index + 1} variant`, 200),
        hashes: filtered,
        ...(record.source
          ? {
              source: cleanText(
                record.source,
                `Record ${index + 1} source`,
                200,
              ),
            }
          : {}),
        reference: cleanText(
          record.reference,
          `Record ${index + 1} reference`,
          1_000,
        ),
        ...(record.firstSeen
          ? {
              firstSeen: cleanText(
                record.firstSeen,
                `Record ${index + 1} first-seen date`,
                80,
              ),
            }
          : {}),
      };
    }),
    // Only added when the source pack actually carried the key. A v1 pack has
    // none, and introducing an empty array would change the canonical payload
    // the signature was computed over, invalidating it.
    ...(Array.isArray(value.fingerprints)
      ? { fingerprints: normalizeFingerprints(value.fingerprints) }
      : {}),
    ...(Array.isArray(value.yara)
      ? {
          yara: value.yara.slice(0, 200).flatMap((raw, index) => {
            const entry = raw as Partial<ThreatIntelPackYara>;
            if (typeof entry?.rules !== "string" || !entry.rules.trim()) {
              return [];
            }
            return [
              {
                namespace: cleanText(
                  entry.namespace ?? `pack-${index + 1}.yar`,
                  `YARA entry ${index + 1} namespace`,
                  120,
                ),
                rules: entry.rules,
              },
            ];
          }),
        }
      : {}),
  };
  return pack;
}

export async function importThreatIntelPack(
  input: string | unknown,
  expectedPublicKey?: string,
): Promise<ImportedIntelPack> {
  const parsed = typeof input === "string" ? JSON.parse(input) : input;
  const pack = normalizePack(parsed);
  const verificationKey = expectedPublicKey?.trim() || pack.publicKey;
  const signatureValid = verificationKey
    ? await verifyEd25519(pack, verificationKey)
    : false;
  const trusted = Boolean(
    signatureValid &&
      expectedPublicKey?.trim() &&
      (!pack.publicKey ||
        pack.publicKey.replace(/^ed25519:/i, "") ===
          expectedPublicKey.trim().replace(/^ed25519:/i, "")),
  );
  const expired = pack.expiresAt
    ? Number.isFinite(Date.parse(pack.expiresAt)) &&
      Date.parse(pack.expiresAt) < Date.now()
    : false;
  const warnings: string[] = [];
  if (!pack.signature) warnings.push("Pack is unsigned.");
  else if (!signatureValid) warnings.push("Ed25519 signature is invalid.");
  else if (!trusted)
    warnings.push(
      "Signature is internally valid but was not anchored to an analyst-supplied public key.",
    );
  if (expired) warnings.push("Pack is expired.");

  const trust: KnownHashRecord["trust"] =
    trusted && !expired ? "verified-pack" : "local-unverified";
  const records: KnownHashRecord[] = pack.records.map((record) => ({
    ...record,
    source: record.source ?? pack.title,
    trust,
    packId: pack.id,
  }));
  // Fingerprints inherit exactly the same trust as the hash records beside
  // them. An unsigned or expired pack contributes leads, not identity, and the
  // score floor keys off `trust` rather than re-deciding this here.
  const fingerprints: KnownFingerprintRecord[] = (pack.fingerprints ?? []).map(
    (record) => ({
      ...record,
      source: record.source ?? pack.title,
      trust,
      packId: pack.id,
    }),
  );
  const yara = (pack.yara ?? []).map((entry) => ({
    ...entry,
    trusted: trusted && !expired,
  }));
  if (yara.length) {
    warnings.push(
      `${yara.length} YARA rule file(s) in this pack must be loaded separately; the pack only carries their source.`,
    );
  }
  return {
    pack,
    records,
    fingerprints,
    yara,
    signatureValid,
    trusted,
    expired,
    warnings,
  };
}

export function activateThreatIntelPack(imported: ImportedIntelPack) {
  installThreatIntelRecords(imported.records);
  installThreatIntelFingerprints(imported.fingerprints);
}

// Authors the fingerprint half of a pack entry from a scan. Without this an
// analyst has to hand-assemble pack JSON around a simhash they copied out of
// the report, and the two halves drift apart: a threshold typed as 0 or a
// simhash with a stray character is silently dropped on load, so a corpus
// quietly fails to protect the samples it claims to.
//
// Running the same validators normalizePack uses means the value produced here
// is exactly the value the loader will keep, with no normalisation step in
// between to invalidate a signature computed over it.
export function fingerprintEntry(
  simhash64: string,
  overrides: Partial<Omit<ThreatIntelPackFingerprint, "simhash64">> = {},
): ThreatIntelPackFingerprint {
  const normalized = cleanSimhash(simhash64);
  if (!normalized) {
    throw new Error(
      "A behavior simhash is 16 hexadecimal characters; copy it from the report.",
    );
  }
  const threshold = cleanThreshold(overrides.threshold ?? 90);
  if (threshold === undefined) {
    throw new Error("A fingerprint threshold must be between 0 and 100.");
  }
  return {
    family: cleanText(overrides.family ?? "Unattributed", "Family", 120),
    variant: cleanText(overrides.variant ?? "Unknown variant", "Variant", 200),
    simhash64: normalized,
    threshold,
    source: cleanText(overrides.source ?? "local", "Source", 200),
    reference: cleanText(
      overrides.reference ?? "urn:jarlab:local-intelligence",
      "Reference",
      1_000,
    ),
    ...(overrides.firstSeen
      ? { firstSeen: cleanText(overrides.firstSeen, "First seen", 80) }
      : {}),
  };
}
